import { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { customerName, simTimeFull } from '@/utils/format';
import {
  recordArtistProgressPhotos,
  recordArtistSessionNote,
  type CapturedPhoto,
} from '@/domain/sessionFlow';
import type { SessionPhotoKind } from '@/domain/types';
import { pickReferences } from '@/data/heroRecords';
import ClientHistoryPanel from '@/components/artist/ClientHistoryPanel';
import {
  Camera,
  CheckCircle2,
  ChevronLeft,
  ClipboardList,
  Plus,
  Upload,
  X,
} from 'lucide-react';

/**
 * The artist's booking record + session wrap-up. Zack: "the manager deals with
 * all the rebooks" — so the artist only reviews the booking and leaves a note.
 * The rebook read + outcome + payment all live on the manager's floor checkout
 * (see RebookOutcomePanel); nothing money- or rebook-related happens here.
 */
export default function InChairRebooking() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const pieces = useBrainStore((s) => s.pieces);

  const booking = bookingId ? bookings[bookingId] : undefined;
  const customer = booking ? customers[booking.customerId] : undefined;
  const artist = booking ? artists[booking.artistId] : undefined;
  const piece = booking?.pieceId ? pieces[booking.pieceId] : undefined;

  // Land straight on the note form when the dashboard's "Finish session" sent us
  // here; otherwise open on the booking record.
  const [mode, setMode] = useState<'details' | 'finish' | 'done'>(
    params.get('finish') ? 'finish' : 'details',
  );
  const [note, setNote] = useState('');
  const [snapped, setSnapped] = useState<CapturedPhoto[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // What the next tap files the photo as. Most in-chair shots are the work.
  const [captureKind, setCaptureKind] = useState<SessionPhotoKind>('progress');

  const snapCandidates = useMemo(
    () => pickReferences(piece?.designType ?? 'sleeve', 6, `${bookingId ?? 'bk'}-snap`),
    [piece?.designType, bookingId],
  );

  // Which sitting this is within the piece: position in `bookingsLinked` ordered
  // by date. Same derivation the customer record uses, so they never disagree.
  const sessionNo = useMemo(() => {
    if (!piece || !booking) return null;
    const linked = piece.bookingsLinked
      .map((id) => bookings[id])
      .filter((b) => b != null)
      .sort((x, y) => (x!.start < y!.start ? -1 : 1));
    const idx = linked.findIndex((x) => x!.id === booking.id);
    return idx >= 0 ? idx + 1 : (piece.sessionsCompleted ?? linked.length) + 1;
  }, [piece, booking, bookings]);

  const pieceLabel = piece
    ? (piece.title ?? `${piece.designType.replace('-', ' ')} on ${piece.placement}`)
    : 'This session';

  const toggleSnap = (url: string) =>
    setSnapped((prev) =>
      prev.some((p) => p.url === url)
        ? prev.filter((p) => p.url !== url)
        : [...prev, { url, kind: captureKind }],
    );

  // Shoot from the device. Held as a client-side data URL, the same way the
  // customer brief takes a reference upload (CustomerBriefControls). It files
  // under whatever tag is currently selected, so an artist can upload the
  // stencil they photographed as a reference and their own shots as progress.
  const fileRef = useRef<HTMLInputElement>(null);
  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') return;
        const url = reader.result;
        setSnapped((prev) =>
          prev.some((p) => p.url === url) ? prev : [...prev, { url, kind: captureKind }],
        );
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  /** Flip a captured photo between the work and the reference it came from. */
  const flipKind = (url: string) =>
    setSnapped((prev) =>
      prev.map((p) =>
        p.url === url ? { ...p, kind: p.kind === 'reference' ? 'progress' : 'reference' } : p,
      ),
    );

  if (!booking || !customer || !artist) {
    return (
      <section className="px-10 py-12">
        <p className="font-serif text-cream-soft">
          Booking {bookingId} not found. Did the deposit not land yet?
        </p>
        <button onClick={() => navigate(-1)} className="btn-quiet mt-4">
          <ChevronLeft size={12} /> Back
        </button>
      </section>
    );
  }

  const artistName = artist.displayName ?? artist.firstName;

  const recordAndFinish = () => {
    // Photos first: the note call treats a blank note as a no-op, so an artist
    // who shot pictures without writing anything must not lose them.
    recordArtistProgressPhotos(booking.id, snapped, sessionNo);
    recordArtistSessionNote(booking.id, note);
    setMode('done');
  };

  if (mode === 'done') {
    return (
      <div
        className="min-h-screen bg-ink-black flex items-center justify-center px-10"
        data-testid="artist-finish-done"
      >
        <div className="max-w-md text-center">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-3xl text-cream mt-4">Session wrapped</h1>
          <p className="font-serif text-cream-soft mt-2">
            {note.trim()
              ? `Your note on ${customerName(customer)} is on their record.`
              : `${customerName(customer)}'s session is wrapped.`}{' '}
            The studio manager takes it from here — rebook, reason, and payment at checkout.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/artist/${artist.id}`)}
            className="btn-primary text-xs mt-6"
            data-testid="artist-finish-back"
          >
            Back to {artistName}&apos;s dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="px-10 py-10 max-w-6xl">
      <button
        type="button"
        onClick={() => navigate(`/artist/${artist.id}`)}
        className="btn-quiet text-[11px] mb-4"
      >
        <ChevronLeft size={12} /> Back to dashboard
      </button>

      {/* Wide working area: the booking + wrap-up on the left, the client's
          history on the right. The history is why the artist no longer needs the
          front-desk customer record. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 items-start">
        <div className="space-y-6">
      {/* The booking record — what the artist needs at a glance. */}
      <div className="panel p-6" data-testid="artist-booking-details">
        <p className="label-small-caps text-gold-bright">Booking</p>
        <h1 className="heading-display text-3xl text-cream mt-2">{customerName(customer)}</h1>
        <p className="font-inter text-[11px] text-cream-soft mt-1 uppercase tracking-widest">
          {simTimeFull(booking.start)} · {Math.round(booking.durationMin / 60)}h · {booking.sessionLength.replace('-', ' ')}
        </p>

        {/* The piece this sitting belongs to, named up front — the artist should
            know what they're working on before they open anything. */}
        {piece ? (
          <div className="mt-4" data-testid="artist-booking-piece">
            <h2 className="heading-display text-xl text-cream">{pieceLabel}</h2>
            <p className="font-inter text-[11px] text-cream-soft mt-1 uppercase tracking-widest">
              {sessionNo != null ? (
                <span className="text-gold-bright">
                  Session {sessionNo}
                  {piece.estimatedTotalSessions ? ` of ${piece.estimatedTotalSessions}` : ''}
                </span>
              ) : null}
              {` · ${piece.placement} · ${piece.hoursCompleted}h of ${piece.estimatedTotalHours}h`}
            </p>
          </div>
        ) : null}

        <p className="font-serif text-cream-soft mt-4">{booking.designBrief}</p>

        {booking.referencePhotos.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="artist-booking-refs">
            {booking.referencePhotos.slice(0, 6).map((src, i) => (
              <img
                key={i}
                src={src}
                alt="reference"
                loading="lazy"
                className="h-20 w-20 object-cover rounded border border-gold-dim/30"
              />
            ))}
          </div>
        ) : null}

      </div>

      {mode === 'details' ? (
        <div>
          <button
            type="button"
            onClick={() => setMode('finish')}
            className="btn-primary text-sm"
            data-testid="artist-finish-session"
          >
            <CheckCircle2 size={14} /> Finish session
          </button>
        </div>
      ) : (
        <div className="panel p-6 space-y-4" data-testid="artist-finish-panel">
          {/* What the note and the photos are of. Without this the artist is
              writing and shooting into a piece they have to remember. */}
          <div className="border-b border-gold-dim/30 pb-3.5">
            <h2 className="heading-display text-xl text-cream" data-testid="artist-wrapup-piece">
              {pieceLabel}
            </h2>
            <p className="font-inter text-[11px] text-cream-soft mt-1.5 uppercase tracking-widest">
              {sessionNo != null && piece?.estimatedTotalSessions ? (
                <span className="text-gold-bright">
                  Session {sessionNo} of {piece.estimatedTotalSessions}
                </span>
              ) : sessionNo != null ? (
                <span className="text-gold-bright">Session {sessionNo}</span>
              ) : null}
              {piece ? ` · ${piece.placement}` : null}
            </p>
          </div>

          <div>
            <p className="label-small-caps flex items-center gap-1.5">
              <ClipboardList size={12} className="text-gold-bright" /> Session note
            </p>
            <p className="font-serif text-[12px] text-cream-soft mt-1">
              Anything worth remembering for next time — it lands on {customer.firstName}&apos;s
              record. Optional.
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="What did you learn in the chair today?"
              className="mt-2 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif focus:outline-none focus:border-gold-bright"
              data-testid="artist-session-note"
            />
          </div>

          {/* Progress photos. Same tap-to-capture the manager's checkout uses;
              they commit to the piece on Record & finish. */}
          <div className="border-t border-gold-dim/30 pt-4">
            <div className="flex items-center justify-between gap-2">
              <p className="label-small-caps text-gold-bright flex items-center gap-1.5">
                <Camera size={12} /> Progress photos
              </p>
              <span className="font-mono text-[10.5px] uppercase tracking-widest text-cream-faint">
                {snapped.length} this session
              </span>
            </div>

            {piece ? (
              <>
                <p className="font-serif text-[11.5px] text-cream-faint mt-1.5">
                  Filed under <em>{pieceLabel}</em>
                  {sessionNo != null ? `, session ${sessionNo}` : ''}. Earlier sessions&apos;
                  photos are on the history to the right.
                </p>

                {/* The + tile opens the picker; captured shots sit beside it so
                    the artist sees this session building up. */}
                <div className="flex flex-wrap items-start gap-1.5 mt-3">
                  <button
                    type="button"
                    onClick={() => setPickerOpen((v) => !v)}
                    aria-expanded={pickerOpen}
                    aria-label="Add progress photos"
                    data-testid="artist-add-photo"
                    className={clsx(
                      'flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed transition',
                      pickerOpen
                        ? 'border-gold-bright text-gold-bright bg-gold/10'
                        : 'border-gold-dim/50 text-cream-soft hover:border-gold-bright hover:text-gold-bright',
                    )}
                  >
                    <Plus size={18} />
                    <span className="font-inter text-[9px] uppercase tracking-widest">Add</span>
                  </button>

                  {snapped.map((photo, i) => {
                    const isRef = photo.kind === 'reference';
                    return (
                      <div
                        key={`${photo.url}-${i}`}
                        className={clsx(
                          'relative h-16 w-16 shrink-0 overflow-hidden rounded-md border ring-2',
                          isRef ? 'border-azure ring-azure' : 'border-mint ring-mint',
                        )}
                        data-testid={`artist-snapped-${i}`}
                      >
                        <img
                          src={photo.url}
                          alt={isRef ? 'captured reference' : 'captured progress'}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => toggleSnap(photo.url)}
                          aria-label="Remove photo"
                          data-testid={`artist-snapped-remove-${i}`}
                          className="absolute top-0.5 right-0.5 rounded-full bg-ink-black/80 p-0.5 text-cream hover:text-blood"
                        >
                          <X size={10} />
                        </button>
                        {/* The tag rides the picture, and is how you change it. */}
                        <button
                          type="button"
                          onClick={() => flipKind(photo.url)}
                          aria-label={`Tagged ${photo.kind}. Change.`}
                          data-testid={`artist-snapped-kind-${i}`}
                          className={clsx(
                            'absolute inset-x-0 bottom-0 font-inter text-[8px] uppercase tracking-wide',
                            // Reference is the notable case, so it carries the colour.
                            // Progress is the default and reads quiet: mint only clears
                            // 4.1:1 behind 8px text either way, which fails AA.
                            isRef ? 'bg-azure text-ink-black' : 'bg-ink-soft text-cream',
                          )}
                        >
                          {isRef ? 'Reference' : 'Progress'}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {pickerOpen ? (
                  <div className="mt-3 rounded-md border border-gold-dim/30 bg-ink-black/40 p-3">
                    {/* Tag first, then shoot — whatever's selected here is what the
                        next tap files the photo as. Each one can be flipped after. */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="label-small-caps text-gold-bright text-[10px]">
                        Tag it, then add
                      </p>
                      <div
                        className="flex overflow-hidden rounded-full border border-gold-dim/40"
                        role="group"
                        aria-label="What are you adding?"
                      >
                        {(['progress', 'reference'] as SessionPhotoKind[]).map((kind) => (
                          <button
                            type="button"
                            key={kind}
                            onClick={() => setCaptureKind(kind)}
                            aria-pressed={captureKind === kind}
                            data-testid={`artist-capture-kind-${kind}`}
                            className={clsx(
                              'px-2.5 py-1 font-inter text-[9px] uppercase tracking-widest transition',
                              captureKind === kind
                                ? kind === 'reference'
                                  ? 'bg-azure text-ink-black'
                                  : 'bg-ink-soft text-cream'
                                : 'text-cream-soft hover:text-cream',
                            )}
                          >
                            {kind}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Upload is the real path — the artist is holding the photo
                        they just took. The stock strip below is the demo fallback. */}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onUpload}
                      className="hidden"
                      data-testid="artist-upload-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="btn-ghost w-full text-xs"
                      data-testid="artist-upload"
                    >
                      <Upload size={12} /> Upload {captureKind} photo
                    </button>

                    <p className="label-small-caps text-cream-faint mt-3 mb-1.5 text-[10px]">
                      Or pick from the studio gallery
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-testid="artist-snap-candidates">
                      {snapCandidates.map((url, i) => {
                        const selected = snapped.some((p) => p.url === url);
                        return (
                          <button
                            type="button"
                            key={`${url}-${i}`}
                            onClick={() => toggleSnap(url)}
                            aria-pressed={selected}
                            aria-label={selected ? 'Remove photo' : 'Capture photo'}
                            data-testid={`artist-snap-candidate-${i}`}
                            className={clsx(
                              'relative h-16 w-16 overflow-hidden rounded-md border transition',
                              selected
                                ? 'border-mint ring-2 ring-mint shadow-gold-glow'
                                : 'border-gold-dim/30 hover:border-gold-bright/70',
                            )}
                          >
                            <img
                              src={url}
                              alt="capture candidate"
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                            {selected ? (
                              <span className="absolute top-1 right-1 rounded-full bg-mint p-0.5 text-ink-black">
                                <CheckCircle2 size={10} />
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <p
                className="font-serif text-[12px] text-cream-faint mt-1.5 italic"
                data-testid="artist-progress-nopiece"
              >
                This booking isn&apos;t linked to a piece, so there&apos;s nothing to file photos
                against. The front desk can link one on the customer record.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={recordAndFinish}
            className="btn-primary text-sm"
            data-testid="artist-record-finish"
          >
            <CheckCircle2 size={14} /> Record &amp; finish
          </button>
        </div>
      )}
        </div>

        {/* RIGHT — the client's history, grouped by piece. */}
        <ClientHistoryPanel
          customer={customer}
          currentPieceId={piece?.id}
          currentBookingId={booking.id}
        />
      </div>
    </section>
  );
}
